//done
//@ts-check
import * as config from "../constants.js";
import {} from "./util.js";
import { createInventoryCard } from "./inventory.js"
import { refreshAlpineInventory } from "./inventoryManager.js";
import { gameState, saveGameState } from "./gameState.js";
import { ensureLifetimeCounters } from "./gameState-core.js";
import { isArtifactUnlocked, unlockArtifactCore, duplicateCraftsAllowed, ownsTechGear, ownsArtifact } from "./command-core.js";
import { addNotification } from "./notifications.js";
import { findArtifact, findItem, findItemsById, getAllArtifacts } from "./util.js";
import { applyCraftingCostScaling } from "./raidUtil.js";
import { getRecipeById, getScaledRecipe, canCraftRecipe as canCraftRecipeCore, craftCreditCost } from "./recipes-core.js";
// Re-exported so existing importers (inventory.js, tests) keep working now that
// the implementations live in a headless module.
export { getRecipeById, getScaledRecipe };

// ============================================================================
// INFINITE SCROLL STATE FOR CRAFTING
// ============================================================================

const craftingScrollState = {
  /** @type {Array<any>} */
  allFilteredRecipes: [],
  /** @type {Array<any>} */
  visibleRecipes: [],
  pageSize: 15,
  /** @type {Object} */
  preComputedMaterials: {},
  /** @type {IntersectionObserver|null} */
  observer: null,
  /** Track the actual end position of loaded recipes */
  loadedUpTo: 0,
  /** @type {NodeJS.Timeout|null} Debounce timeout ID to prevent rapid successive loads */
  loadDebounceTimeout: null,

  reset() {
    this.allFilteredRecipes = [];
    this.visibleRecipes = [];
    this.preComputedMaterials = {};
    this.loadedUpTo = 0;
    if (this.loadDebounceTimeout) {
      clearTimeout(this.loadDebounceTimeout);
      this.loadDebounceTimeout = null;
    }
  },

  hasMoreToLoad() {
    return this.loadedUpTo < this.allFilteredRecipes.length;
  },

  loadMore() {
    if (!this.hasMoreToLoad()) {
      return false;
    }

    const end = Math.min(this.loadedUpTo + this.pageSize, this.allFilteredRecipes.length);
    // Load from start (0) to current end position
    this.visibleRecipes = this.allFilteredRecipes.slice(0, end);
    this.loadedUpTo = end;

    // Set debounce timeout for 300ms to prevent rapid loads
    this.loadDebounceTimeout = setTimeout(() => {
      this.loadDebounceTimeout = null;
    }, 300);

    return true;
  }
};

// ============================================================================
// PROGRESSION SCALING INTEGRATION
// ============================================================================

/**
 * Get a recipe with scaled inputs applied
 * @param {Object} recipe - The recipe object
 * @returns {Object} - The recipe with scaled inputs
 */
// getScaledRecipe / getRecipeById now live in recipes-core.js so they can be
// used headlessly (this file pulls in the crafting UI). Re-exported below.

// Artifact Crafting System
export function canCraftArtifact(artifactKey) {
    const artifact = findArtifact('id',artifactKey);
    if (!artifact) return false;

    // Check if player's faction matches artifact faction (or artifact is neutral)
    if (artifact.faction !== 'neutral' && gameState.character.faction !== artifact.faction) {
        return false;
    }

    // The design must be unlocked (blueprint consumed) before it can be crafted
    if (!isArtifactUnlocked(artifactKey)) return false;

    // Find the recipe for this artifact and check if it can be crafted
    const recipe = getRecipeById(artifactKey);
    if (!recipe) return false;

    // Use the existing canCraftRecipe function to check materials
    return canCraftRecipe(recipe);
}

export function craftItem(itemId, isArtifact) {
    // Check if current character is on marketplace
    if (gameState.character && gameState.character.isMarketplace) {
        addNotification('Cannot craft with a character that is contracted out on the marketplace', 'error');
        return false;
    }

    if (isArtifact) {
        return craftArtifact(itemId);
    } else {
        return craftComponent(itemId);
    }
}

function craftComponent(itemId, quantity = 1) {
    const item = findItem('id', itemId);
    if (!item) {
        addNotification('Unknown component recipe!', 'error');
        return false;
    }

    // Get recipe with scaling applied
    const recipe = getRecipeById(itemId);

    if (!recipe) {
        addNotification('Recipe not found!', 'error');
        return false;
    }

    // One craft per design per character for durable tech gear (equip-once,
    // one per skill). Consumables and intermediate components stay repeatable.
    if (item.type === 'techGear' && !duplicateCraftsAllowed() && ownsTechGear(gameState.character, itemId)) {
        addNotification(`${item.name} is already crafted - one per character.`, 'error');
        return false;
    }

    // Calculate total credit cost for all crafts
    const creditCost = calculateBulkCraftingCost(itemId, quantity);
    const playerCredits = gameState.player?.credits || 0;

    // Check materials for all quantities
    for (const input of recipe.inputs) {
        const available = gameState.character.inventory[input.id] || 0;
        const needed = input.qty * quantity;
        if (available < needed) {
            addNotification(`Missing materials! Need ${needed - available} more ${findItem('id', input.id)?.name || input.id}`, 'error');
            return false;
        }
    }

    // Check credits
    if (playerCredits < creditCost) {
        addNotification(`Not enough credits! Need ${creditCost}, have ${playerCredits}`, 'error');
        return false;
    }

    // Consume materials
    for (const input of recipe.inputs) {
        gameState.character.inventory[input.id] -= input.qty * quantity;
        if (gameState.character.inventory[input.id] <= 0) {
            delete gameState.character.inventory[input.id];
        }
    }

    // Deduct credits
    gameState.player.credits -= creditCost;

    // Add crafted items to inventory
    if (!gameState.character.inventory[item.id]) {
        gameState.character.inventory[item.id] = 0;
    }
    gameState.character.inventory[item.id] += quantity;

    refreshAlpineInventory();
    const message = quantity > 1
        ? `Crafted ${quantity}x ${item.name} for ${creditCost}💰!`
        : `Crafted ${item.name} for ${creditCost}💰!`;
    addNotification(message, 'success');
    saveGameState();

    return true;
}

/**
 * Get the cost percentage for a given quantity (inverse of discount)
 * Cost tiers: 1x=100%, 2-5x=85%, 6-10x=75%, 11-20x=65%, 20+=50%
 * @param {number} quantity - Quantity being crafted
 * @returns {number} - Cost percentage you pay (50-100)
 */
export function getBulkCraftingCostPercent(quantity = 1) {
    if (quantity >= 20) return 50;
    if (quantity >= 11) return 65;
    if (quantity >= 6) return 75;
    if (quantity >= 2) return 85;
    return 100;
}

/**
 * Calculate the credit cost for crafting, including bulk discounts
 * Higher quantities get better discounts (lower per-unit cost)
 * @param {string} itemId - The item to craft
 * @param {number} quantity - How many to craft
 * @returns {number} - Total credit cost with discounts applied
 */
export function calculateBulkCraftingCost(itemId, quantity = 1) {
    const item = findItem('id', itemId);
    if (!item) return 0;

    // Base cost from rarity, scaled by progressionScaling.craftingCostScaling.
    // Lives in recipes-core so the simulator and Auto-Fabricator charge the
    // same thing - keep parity.
    const baseCost = craftCreditCost(itemId);

    // Get cost percentage based on quantity (higher qty = lower percentage = more discount)
    const costPercent = getBulkCraftingCostPercent(quantity);

    // Calculate total cost with bulk discount applied
    const costPerUnit = (baseCost * costPercent) / 100;
    return Math.ceil(costPerUnit * quantity);
}

export function craftArtifact(artifactKey) {
    const artifact = findArtifact('id', artifactKey);
    if (!artifact) {
        addNotification('Unknown artifact recipe!', 'error');
        return false;
    }

    if (!canCraftArtifact(artifactKey)) {
        addNotification('Cannot craft this artifact - missing materials or wrong faction!', 'error');
        return false;
    }

    // One craft per design per character (artifacts are equip-once; a duplicate
    // earns no marks and has no use). Toggle via allowDuplicateCrafts.
    if (!duplicateCraftsAllowed() && ownsArtifact(gameState.character, artifactKey)) {
        addNotification(`${artifact.name} is already crafted - one per character.`, 'error');
        return false;
    }

    // Get recipe with scaling applied
    const recipe = getRecipeById(artifactKey);
    if (!recipe) {
        addNotification('Artifact recipe not found!', 'error');
        return false;
    }

    // Consume materials from recipe (blueprints are NO LONGER recipe inputs -
    // they're consumed once by unlockArtifact to unlock the design; crafting
    // an unlocked design costs materials only)
    for (const input of recipe.inputs) {
        gameState.character.inventory[input.id] -= input.qty;
        if (gameState.character.inventory[input.id] <= 0) {
            delete gameState.character.inventory[input.id];
        }
    }

    // Create artifact instance with unique ID
    const craftedArtifact = {
        key: Date.now() + Math.random(), // Unique ID
        id: artifact.id,
        name: artifact.name,
        type: artifact.type,
        slot: artifact.slot,
        skill: artifact.skill,
        effects: artifact.effects,
        description: artifact.description,
        short: artifact.short,
        faction: artifact.faction,
        rarity: artifact.rarity,
        icon: artifact.icon,
        crafted: true
    };

    // Add to artifacts collection
    if (!gameState.character.artifacts) gameState.character.artifacts = [];
    const firstOfType = !gameState.character.artifacts.some(a => a.id === craftedArtifact.id);
    gameState.character.artifacts.push(craftedArtifact);

    // Lifetime counter for the Command Marks formula: DISTINCT designs only.
    // Command rewards novel engineering, not mass production - duplicate
    // crafts (deep tours farming blueprints at T3) earn no extra marks.
    ensureLifetimeCounters(gameState.character);
    if (firstOfType) {
        gameState.character.lifetime.artifactsCrafted += 1;
    }

    refreshAlpineInventory();
    addNotification(`Crafted ${artifact.name}!`, 'success');
    saveGameState();

    return true;
}

/**
 * Unlock an artifact design for the account by consuming one of its blueprints.
 * One-time per design; banks the unlock marks. After this, the design can be
 * crafted from materials alone (by any character).
 */
export function unlockArtifact(artifactKey) {
    const artifact = findArtifact('id', artifactKey);
    if (!artifact) {
        addNotification('Unknown artifact design!', 'error');
        return false;
    }
    const result = unlockArtifactCore(artifactKey);
    if (!result.success) {
        const msg = result.reason === 'already-unlocked' ? 'Design already unlocked.' : 'You need this design\'s blueprint to unlock it.';
        addNotification(msg, 'error');
        return false;
    }
    refreshAlpineInventory();
    addNotification(`🔓 ${artifact.name} design unlocked! +${result.marks} 🎖️ at promotion`, 'success');
    saveGameState();
    return true;
}

/**
 * Get a recipe by item ID, with scaling applied based on item rarity
 * @param {string} itemId - The ID of the target item
 * @returns {Object|null} - The recipe with scaling applied, or null if not found
 */


/**
 * Collect an item's full crafting tree: the item plus every component in its
 * recipe chain that is itself craftable (raw drop-only materials excluded).
 *
 * Returns item ids ordered deepest-first - the order the player should craft
 * in - with the target last. Cycle-safe. Powers the crafting modal's
 * component-tree filter (opened from the Gear page).
 */
export function getCraftingTree(itemId) {
    const depths = new Map(); // itemId -> deepest depth seen (target = 0)

    const walk = (id, depth, pathSeen) => {
        const recipe = getRecipeById(id);
        if (!recipe) return; // raw material - not part of the craftable tree
        if ((depths.get(id) ?? -1) < depth) {
            depths.set(id, depth);
        }
        if (pathSeen.has(id)) return; // cycle guard
        const nextSeen = new Set(pathSeen).add(id);
        (recipe.inputs || []).forEach(input => walk(input.id, depth + 1, nextSeen));
    };

    walk(itemId, 0, new Set());
    return [...depths.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([id]) => id);
}

export function getAllRecipes() {
    const recipes = [];

    // Add component recipes (uncommon, rare, epic) with scaling applied
    const craftingRecipes = [
        ...config.recipesUncommon,
        ...config.recipesRare,
        ...config.recipesEpic
    ];

    // Batch lookup all target items at once instead of individual lookups
    const targetIds = craftingRecipes.map(r => r.target);
    const itemsMap = findItemsById(targetIds);

    craftingRecipes.forEach(recipe => {
        const item = itemsMap[recipe.target];
        if (item) {
            // Get scaled recipe to apply progression scaling
            const scaledRecipe = getRecipeById(recipe.target);
            recipes.push({
                ...(scaledRecipe || recipe),
                itemRarity: item.rarity,
                itemType: item.type,
                techniqueId: item.techniqueId,
            });
        }
    });

    // Add artifact recipes with scaling applied
    config.recipesArtifact.forEach(recipe => {
        const artifact = findArtifact('id', recipe.target);
        if (artifact) {
            // Get scaled recipe to apply progression scaling
            const scaledRecipe = getRecipeById(recipe.target);
            recipes.push({
                ...(scaledRecipe || recipe),
                itemRarity: artifact.rarity,
                itemType: artifact.type
            });
        }
    });

    return recipes;
}

export function getAvailableArtifactRecipes() {
    const recipes = [];
    const playerFaction = gameState.character.faction;

    for (const [key, artifact] of Object.entries(getAllArtifacts())) {
        // Only show recipes for player's faction or neutral artifacts
        if (artifact.faction === 'neutral' || artifact.faction === playerFaction) {
            recipes.push({
                key,
                ...artifact,
                canCraft: canCraftArtifact(key)
            });
        }
    }

    return recipes;
}

export function showCraftingModal(techniqueId = null) {
    const modal = document.getElementById('crafting-modal');
    if (!modal) {
        createCraftingModal();
        return showCraftingModal(techniqueId);
    }

    // If a techniqueId is provided, filter to techGear type for that technique
    const filterType = document.getElementById('filter-type');
    if (filterType) {
        filterType.value = techniqueId ? 'techGear' : '';
    }

    if (techniqueId) {
        // Store the techniqueId so refreshCraftingList can use it for additional filtering
        modal.dataset.filteredTechniqueId = techniqueId;
    } else {
        // Clear any previous technique filter
        delete modal.dataset.filteredTechniqueId;
    }

    refreshCraftingList();
    modal.classList.remove('hidden');
}

// Event handler wrapper for addEventListener usage (ignores event parameter)
export function openCraftingModal() {
    showCraftingModal();
}

/**
 * Open the crafting modal filtered to one item's component tree (the item
 * plus all craftable components, deepest-first). Cards render exactly as in
 * normal browsing - this is a filter/sort mode only.
 */
export function showCraftingTreeModal(itemId) {
    let modal = document.getElementById('crafting-modal');
    if (!modal) {
        createCraftingModal();
        modal = document.getElementById('crafting-modal');
    }

    modal.dataset.treeFilterItemId = itemId;
    delete modal.dataset.filteredTechniqueId;

    // Tree mode shows the whole chain: neutralize the manual filters
    const filterType = document.getElementById('filter-type');
    const filterRarity = document.getElementById('filter-rarity');
    const filterCraftable = document.getElementById('filter-craftable');
    if (filterType) filterType.value = '';
    if (filterRarity) filterRarity.value = '';
    if (filterCraftable) filterCraftable.checked = false;

    refreshCraftingList();
    modal.classList.remove('hidden');
}

export function clearCraftingTreeFilter() {
    const modal = document.getElementById('crafting-modal');
    if (!modal) return;
    delete modal.dataset.treeFilterItemId;
    refreshCraftingList();
}

export function createCraftingModal() {
    // Build type filter options from config
    const craftableTypes = ['material', 'consumable', 'techGear', 'artifact'];
    const typeOptions = craftableTypes.map(type => {
        const typeName = config.itemTypes[type]?.name || type;
        return `<option value="${type}">${typeName}</option>`;
    }).join('');

    // Build rarity filter options from config
    const craftableRarities = ['uncommon', 'rare', 'epic', 'unique'];
    const rarityOptions = craftableRarities.map(rarity => {
        const rarityName = config.itemRarities[rarity]?.name || rarity;
        return `<option value="${rarity}">${rarityName}</option>`;
    }).join('');

    const modal = document.createElement('div');
    modal.id = 'crafting-modal';
    modal.className = 'modal hidden';
    modal.innerHTML = `
        <div class="modal-content crafting-modal-content max-w-6xl w-full max-h-screen">
            <div class="modal-header">
                <h3 class="modal-title">Crafting</h3>
                <button id="close-crafting-modal" class="close-btn">&times;</button>
            </div>
            <div class="modal-body overflow-hidden">
                <div class="flex gap-2 p-4 border-b border-gray-600">
                    <select id="filter-type" class="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm">
                        <option value="">All Types</option>
                        ${typeOptions}
                    </select>
                    <select id="filter-rarity" class="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm">
                        <option value="">All Rarities</option>
                        ${rarityOptions}
                    </select>
                    <label class="flex items-center gap-1 text-sm">
                        <input type="checkbox" id="filter-craftable" class="form-checkbox">
                        <span>Craftable Only</span>
                    </label>
                    <span id="crafting-tree-chip" class="hidden items-center gap-2 px-3 py-1 rounded-full border border-game-accent text-game-accent text-xs font-bold">
                        <!-- populated by refreshCraftingList when the tree filter is active -->
                    </span>
                </div>
                <div id="crafting-recipe-list" class="grid gap-4 p-4" style="grid-template-columns: repeat(auto-fit, 13rem); justify-content: center; height: calc(70vh - 200px); overflow-y: auto; scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748;">
                    <!-- Recipes will be populated here -->
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Add event listener for close button
    const closeBtn = document.getElementById('close-crafting-modal');
    closeBtn.addEventListener('click', () => {
        modal.classList.add('hidden');
    });

    // Add filter listeners
    const filterType = document.getElementById('filter-type');
    const filterRarity = document.getElementById('filter-rarity');
    const filterCraftable = document.getElementById('filter-craftable');

    filterType.addEventListener('change', () => {
        // Clear technique + tree filters when user manually changes the type filter
        delete modal.dataset.filteredTechniqueId;
        delete modal.dataset.treeFilterItemId;
        refreshCraftingList();
    });

    filterRarity.addEventListener('change', () => {
        // Manual rarity browsing exits tree mode too (a rarity slice of a
        // dependency chain reads as missing items, not as a filter)
        delete modal.dataset.treeFilterItemId;
        refreshCraftingList();
    });

    // Craftable-only applies WITHIN tree mode (show me what I can craft next)
    filterCraftable.addEventListener('change', () => refreshCraftingList());

    // Close modal when clicking outside (only if mousedown started on background)
    let mousedownTarget = null;
    modal.addEventListener('mousedown', (e) => {
        mousedownTarget = e.target;
    });
    modal.addEventListener('click', (e) => {
        if (e.target === modal && mousedownTarget === modal) {
            modal.classList.add('hidden');
        }
    });
}

/**
 * Materials check for the ACTIVE character. The implementation lives in
 * recipes-core.js so the headless side (Auto-Fabricator) shares it; this keeps
 * the one-argument signature every UI call site already uses.
 *
 * Artifact blueprints are regular recipe inputs (slug ids, matching how drops
 * bank) - the old name-keyed special check never saw dropped blueprints at all
 * ("X Blueprint" vs "x-blueprint").
 */
export function canCraftRecipe(scaledRecipe, character = gameState.character) {
    return canCraftRecipeCore(scaledRecipe, character);
}

function createRecipeCard(recipe, preComputedMaterials) {
    // Recipe is already scaled from getAllRecipes()
    // For artifacts, get full item data
    const item = recipe.itemType === 'artifact' ? findArtifact('id', recipe.target) : findItem('id', recipe.target);
    return item ? createInventoryCard(item, 'crafting', recipe, preComputedMaterials, { inModal: true }) : '';
}

function refreshCraftingList() {
    const modal = document.getElementById('crafting-modal');
    const recipeList = document.getElementById('crafting-recipe-list');
    const filterType = document.getElementById('filter-type').value;
    const filterRarity = document.getElementById('filter-rarity').value;
    const filterCraftable = document.getElementById('filter-craftable').checked;
    const filteredTechniqueId = modal?.dataset.filteredTechniqueId;
    const treeFilterItemId = modal?.dataset.treeFilterItemId;

    let recipes = getAllRecipes();

    if (treeFilterItemId) {
        // Component-tree mode: only the target + its craftable components,
        // ordered deepest-first (craft order). Replaces type/rarity/technique
        // filtering; Craftable Only still applies within the tree.
        const treeOrder = getCraftingTree(treeFilterItemId);
        const orderIndex = new Map(treeOrder.map((id, i) => [id, i]));
        recipes = recipes.filter(r => orderIndex.has(r.target));
        recipes.sort((a, b) => orderIndex.get(a.target) - orderIndex.get(b.target));
    } else {
        // Apply filters
        if (filterType) {
            recipes = recipes.filter(r => r.itemType === filterType);
        }
        if (filterRarity) {
            recipes = recipes.filter(r => r.itemRarity === filterRarity);
        }
        // If a technique filter is active, filter by technique ID
        if (filteredTechniqueId) {
            recipes = recipes.filter(r => r.techniqueId === filteredTechniqueId);
        }
    }
    if (filterCraftable) {
        recipes = recipes.filter(r => canCraftRecipe(r));
    }

    // Tree filter chip: shows the target + item count, ✕ returns to browsing
    const treeChip = document.getElementById('crafting-tree-chip');
    if (treeChip) {
        if (treeFilterItemId) {
            const targetItem = findItem('id', treeFilterItemId) || findArtifact('id', treeFilterItemId);
            treeChip.innerHTML = `🌳 Components: <b>${targetItem?.name || treeFilterItemId}</b>
                <span class="text-gray-400 font-normal">(${recipes.length} craftable, deepest first)</span>
                <button onclick="clearCraftingTreeFilter()" class="text-gray-400 hover:text-white font-bold ml-1" title="Back to all recipes">✕</button>`;
            treeChip.classList.remove('hidden');
            treeChip.classList.add('inline-flex');
        } else {
            treeChip.classList.add('hidden');
            treeChip.classList.remove('inline-flex');
        }
    }

    // Pre-compute all materials for all recipes in one batch lookup
    const allMaterialIds = [];
    recipes.forEach(recipe => {
        if (recipe.inputs) {
            recipe.inputs.forEach(input => {
                if (!allMaterialIds.includes(input.id)) {
                    allMaterialIds.push(input.id);
                }
            });
        }
    });
    const preComputedMaterials = allMaterialIds.length > 0 ? findItemsById(allMaterialIds) : {};

    // Reset scroll state with new filtered recipes
    craftingScrollState.reset();
    craftingScrollState.allFilteredRecipes = recipes;
    craftingScrollState.preComputedMaterials = preComputedMaterials;

    // Load initial batch
    craftingScrollState.loadMore();
    if (recipeList) {
        renderCraftingRecipes(recipeList);
        // Set up infinite scroll observer
        setupCraftingScrollObserver(recipeList);
    }
}

/**
 * Render the visible recipes to the DOM
 * @param {HTMLElement} recipeList - The recipe list container
 */
function renderCraftingRecipes(recipeList) {
    recipeList.innerHTML = '';
    craftingScrollState.visibleRecipes.forEach(recipe => {
        const cardHtml = createRecipeCard(recipe, craftingScrollState.preComputedMaterials);
        recipeList.innerHTML += cardHtml;
    });

    // Add loading indicator if there are more recipes to load
    if (craftingScrollState.visibleRecipes.length < craftingScrollState.allFilteredRecipes.length) {
        const loadingEl = document.createElement('div');
        loadingEl.id = 'crafting-scroll-trigger';
        loadingEl.style.height = '1px';
        recipeList.appendChild(loadingEl);
    }

    // Re-attach Tippy tooltips to the freshly-rendered card artwork (innerHTML
    // replacement above drops any previous instances)
    window.reinitializeInventoryTooltips?.();
}

/**
 * Set up Intersection Observer for infinite scroll
 * @param {HTMLElement} recipeList - The recipe list container
 */
function setupCraftingScrollObserver(recipeList) {
    // Remove old observer if it exists
    if (craftingScrollState.observer) {
        craftingScrollState.observer.disconnect();
    }

    let pendingLoad = false;

    // Create observer to trigger load more when user scrolls near bottom
    craftingScrollState.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !pendingLoad) {
                pendingLoad = true;
                const didLoad = craftingScrollState.loadMore();

                if (didLoad) {
                    renderCraftingRecipes(recipeList);

                    // Wait before re-observing to allow the DOM to settle
                    setTimeout(() => {
                        const newTrigger = document.getElementById('crafting-scroll-trigger');
                        if (newTrigger && craftingScrollState.observer) {
                            // Unobserve old trigger first
                            if (entry.target) {
                                craftingScrollState.observer.unobserve(entry.target);
                            }
                            craftingScrollState.observer.observe(newTrigger);
                        }
                        pendingLoad = false;
                    }, 100);
                } else {
                    pendingLoad = false;
                }
            }
        });
    }, {
        root: recipeList,
        rootMargin: '100px',
        threshold: 0
    });

    // Observe the initial trigger element
    const trigger = document.getElementById('crafting-scroll-trigger');
    if (trigger) {
        craftingScrollState.observer.observe(trigger);
    }
}